import { Component, OnInit, Input, SimpleChanges, Output, EventEmitter, Renderer2, ElementRef } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ChatbotService } from '../../../services/chatbot-services/chatbot.service';
import { PrivilegesService } from "../../../services/privileges-service/privileges.service"
import { SelectedModuleService } from '../../../services/selected-module-service/selected-module.service';
import { LoggerService } from '../../../services/logger-services/logger.service';
import { DataSetVdb } from '../../models/chatbot.models';

/**
 * NewchatComponent handles the initialization and display of new chat interface
 * including popular questions, module selection, and user interactions
 */
@Component({
  selector: 'app-newchat',
  templateUrl: './newchat.component.html',
  styleUrls: ['./newchat.component.css']
})
export class NewchatComponent implements OnInit {
  /** User's first name for personalization */
  @Input() userFName!: string;
  
  /** Current module name */
  @Input() moduleName!: string;
  
  /** Event emitter for button click actions */
  @Output() buttonClick = new EventEmitter();
  
  /** Domain ID from environment */
  DomainID: any;
  
  /** User ID from environment */
  UserID: any;
  
  /** Array of popular questions */
  popularQuestions: any;
  
  /** Flag to show/hide popular questions section */
  popularQuestionsShow: boolean | undefined;
  
  /** Flag to control loader visibility */
  loaderShow: boolean = false;
  
  /** Flag indicating if static questions are available */
  isStaticQavailable: boolean = false;
  
  /** Flag indicating if dynamic questions are available */
  isDynamicQavailable: boolean = false;
  
  /** Chatbot name */
  chatBotName: string = "";
  
  /** Flag to show/hide disclaimer */
  isDisclaimer: boolean = true;
  
  /** Flag indicating if popular questions should be shown at start */
  isPopularQuestionStart: boolean = false;
  
  /** Configuration data object */
  configdata: any;
  
  /** Currently selected domain name */
  selectedDomainName: any;
  
  /** Array of vector database datasets */
  vectorDbDatasets: DataSetVdb[] = [];
  
  /** User privileges array */
  Privileges: any;

  /**
   * Error formatting function for consistent error logging
   * @param error - The error object to format
   * @returns Formatted error string with component identifier
   */
  private readonly NewchatError = (error: any) =>
    `[NewchatComponent] - ${JSON.stringify(error)}`

  /**
   * Constructor - Initializes component with required services
   * @param chatbotService - Service for chatbot operations
   * @param renderer - Angular renderer for DOM manipulation
   * @param el - Element reference for DOM access
   * @param privilegesService - Service for handling user privileges
   * @param selectedModuleService - Service for managing selected module state
   * @param loggerService - Service for error logging
   */
  constructor(
    private readonly chatbotService: ChatbotService,
    private readonly renderer: Renderer2,
    private readonly el: ElementRef,
    private readonly privilegesService: PrivilegesService,
    private readonly selectedModuleService: SelectedModuleService,
    private readonly loggerService: LoggerService
  ) { }

  /**
   * Angular lifecycle hook - Component initialization
   * Initializes component state, loads configuration, and sets up vector databases
   * @returns Promise<void>
   */
  async ngOnInit(): Promise<void> {
    try {
      this.getConfigData();
      this.popularQuestionsShow = false;
      this.popularQuestions = [];
      this.moduleName = environment.DomainName;
      this.UserID = environment.LoginUserID;
      this.DomainID = environment.APPModuleID;
      this.Privileges = environment.Privileges;
      await this.initializeVectorDbDatasets();
    } catch (error: any) {
      this.loggerService.logError(this.NewchatError(error), "ngOnInit");
    }
  }

  /**
   * Initializes vector database datasets from environment or service
   * Attempts to load from environment first, falls back to service call if needed
   * @returns Promise<void>
   */
  public async initializeVectorDbDatasets(): Promise<void> {
    try {
      // Check if data is available in environment
      if (environment &&
        environment.GlobalInfo["vectorDbDatasets"]?.length > 0 &&
        environment.GlobalInfo["Privileges"]?.length > 0) {
        this.vectorDbDatasets = environment.GlobalInfo["vectorDbDatasets"];
        this.Privileges = environment.GlobalInfo["Privileges"];
        this.subscribeToSelectedModule();
      } else {
        // Fallback to service call
        try {
          this.showLoader();
          const result = await this.privilegesService.getPrivilegesList(this.UserID);
          this.vectorDbDatasets = result.vectorDbDatasets;
          this.Privileges = result.cookiesArray;
          this.subscribeToSelectedModule();
        } catch (error: any) {
          this.hideLoader();
          this.loggerService.logError(this.NewchatError(error), "initializeVectorDbDatasets");
        }
      }
    } catch (error: any) {
      this.loggerService.logError(this.NewchatError(error), "initializeVectorDbDatasets");
    }
  }

  /**
   * Subscribes to selected module changes and handles module selection logic
   * Sets up reactive subscription to module changes and loads popular questions
   * @returns void
   */
  public subscribeToSelectedModule(): void {
    try {
      this.selectedModuleService.currentSelectedValue.subscribe(value => {
        if (value) {
          // Use selected value from service
          this.selectedDomainName = value.selectedVectorDbDataset;
          this.GetPopularQuestions(this.UserID, this.DomainID);
        } else {
          // Use default value if no selection exists
          const defaultValue = this.vectorDbDatasets.length > 0 ? this.vectorDbDatasets[0] : null;
          this.selectedDomainName = defaultValue;
          this.selectedModuleService.changeSelectedValue({ selectedVectorDbDataset: defaultValue });
        }
      });
      this.hideLoader();
    } catch (error: any) {
      this.loggerService.logError(this.NewchatError(error), "subscribeToSelectedModule");
    }
  }

  /**
   * Retrieves and processes configuration data from chatbot service
   * Replaces template placeholders with actual user and module data
   * @returns void
   */
  getConfigData(): void {
    try {
      this.configdata = this.chatbotService.getConfigData();
      // Replace template placeholders with actual values
      this.configdata.IntroDetails = this.configdata.IntroDetails.replace("{{userFName}}", this.userFName);
      this.configdata.PopularQuestionContent = this.configdata.PopularQuestionContent.replace("{{moduleName}}", this.moduleName);
    } catch (error: any) {
      this.loggerService.logError(this.NewchatError(error), "getConfigData");
    }
  }

  /**
   * Angular lifecycle hook - Handles input property changes
   * Updates component state when input properties change
   * @param changes - SimpleChanges object containing changed properties
   * @returns void
   */
  ngOnChanges(changes: SimpleChanges): void {
    try {
      if (changes['moduleName']) {
        this.moduleName = environment.DomainName;
      }
      this.chatBotName = environment.chatbotName;
    } catch (error: any) {
      this.loggerService.logError(this.NewchatError(error), "ngOnChanges");
    }
  }

  /**
   * Shows the loading indicator
   * Sets loaderShow flag to true to display loading spinner
   * @returns void
   */
  showLoader(): void {
    try {
      this.loaderShow = true;
    } catch (error: any) {
      this.loggerService.logError(this.NewchatError(error), "showLoader");
    }
  }

  /**
   * Hides the loading indicator
   * Sets loaderShow flag to false to hide loading spinner
   * @returns void
   */
  hideLoader(): void {
    try {
      this.loaderShow = false;
    } catch (error: any) {
      this.loggerService.logError(this.NewchatError(error), "hideLoader");
    }
  }

  /**
   * Retrieves and processes popular questions for the selected domain
   * Filters questions by module, shuffles them, and categorizes by type
   * @param UserID - The user identifier
   * @param DomainID - The domain identifier
   * @returns void
   */
  /* istanbul ignore next */
  GetPopularQuestions(UserID: any, DomainID: any): void {
    try {
      // Determine domain value from selected domain or default
      let domainValue = this.selectedDomainName?.value || (this.vectorDbDatasets.length > 0 ? this.vectorDbDatasets[0]?.value : "");
      
      // Override with actual module name if configured for module-wise questions
      if (this.configdata.isPopularQuestionModuleWise) {
        domainValue = this.configdata.actual_module_Name;
      }

      if (domainValue != "") {
        this.showLoader();
        this.chatbotService.getJSON().subscribe(data => {
          if (data !== null && data !== undefined) {
            this.popularQuestionsShow = true;
            
            // Filter and map questions for the selected module
            let questions = data.filter((item: any) => item.Module === domainValue)
              .map((item: any) => ({
                moduleID: item.Module,
                priority: item.Priority,
                question: item.Question,
                type: "S",
                tenantID: 1
              }));

            // Shuffle the questions array randomly
            questions.sort(() => Math.random() - 0.5); // NOSONAR: Safe - used for UI shuffling only, not security

            // Take first 5 questions for display
            this.popularQuestions = questions.slice(0, 5);
          }
          
          // Check for static questions availability
          this.isStaticQavailable = true;
          let staticQ = data?.filter((item: { type: string; }) =>
            item.type == 'S'
          );

          if (staticQ != null && staticQ.length > 0) {
            this.isStaticQavailable = true;
          }

          // Check for dynamic questions availability
          let DynamiQ = data?.filter((item: { type: string; }) =>
            item.type == 'D'
          );
          if (DynamiQ != null && DynamiQ.length > 0) {
            this.isDynamicQavailable = true;
          }
          
          this.hideLoader();
        });
      }
    } catch (error: any) {
      this.loggerService.logError(this.NewchatError(error), "GetPopularQuestions");
    }
  }

  /**
   * Handles popular question click events
   * Emits the selected question to parent component
   * @param question - The selected question string
   * @returns void
   */
  PopularQuestionClick(question: string): void {
    try {
      this.buttonClick.emit(question);
    } catch (error: any) {
      this.loggerService.logError(this.NewchatError(error), "PopularQuestionClick");
    }
  }

  /**
   * Handles UI element click events for navigation/toggle functionality
   * Manages CSS classes and configuration state based on clicked element
   * @param elem - The element identifier that was clicked
   * @returns void
   */
  handleClick(elem: any): void {
    try {
      const divElement = this.el.nativeElement.querySelector('.mover');
      
      if (elem === "link2") {
        // Handle "link2" click - move to right and disable popular questions
        this.configdata.isPopularQuestionStart = false;
        this.renderer.addClass(divElement, 'right');
        return;
      } else {
        // Handle other clicks - enable popular questions
        this.configdata.isPopularQuestionStart = true;
      }
      
      // Remove right positioning class
      this.renderer.removeClass(divElement, 'right');
    } catch (error: any) {
      this.loggerService.logError(this.NewchatError(error), "handleClick");
    }
  }
}